import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import Footer from "@/components/Footer";
import { useEffect, useState } from "react";
import SEO from "@/components/SEO";
import { useLanguage } from "@/context/LanguageContext";
import Breadcrumbs from "@/components/Breadcrumbs";

const AIToolEvaluationPromptGenerator = () => {
  const [isLoading, setIsLoading] = useState(true);
  const { t } = useLanguage();

  useEffect(() => {
    setIsLoading(false);
    // No need to load anything separately - iframe handles everything
  }, []);

  return (
    <div className="min-h-screen">
      {/* SEO: Localized metadata for AI Tool Evaluation, targeting Heilbronn startups and OD context */}
      <SEO
        title={t("seo.tools.ai_evaluation.title")}
        description={t("seo.tools.ai_evaluation.description")}
        keywords={t("seo.tools.ai_evaluation.keywords").split(",").map((k) => k.trim())}
        url="https://rassner-dev.org/tools/ai-tool-evaluation-prompt-generator"
        type="software"
        applicationCategory="BusinessApplication"
        operatingSystem="Web"
        price="0"
        currency="EUR"
      />

      <main id="main-content" className="container mx-auto px-2 sm:px-4 py-12">
        <Breadcrumbs />

        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 animate-fade-in">
            {t("tools.aiToolEvaluationPromptGenerator.name")}
          </h1>
          <p className="text-xl text-muted-foreground mb-12 animate-fade-in" style={{ animationDelay: "100ms" }}>
            {t("tools.aiToolEvaluationPromptGenerator.description")}
          </p>

          {/* Tool Container */}
          <div
            className="bg-background rounded-lg border shadow-lg overflow-hidden animate-scale-in"
            style={{ animationDelay: "200ms" }}
          >
            {isLoading ? (
              <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                  <p className="text-muted-foreground">Loading AI Tool Evaluation Prompt Generator...</p>
                </div>
              </div>
            ) : (
              <iframe
                src="/tools/AI_Tool_Evaluation_Prompt_Generator/index.html"
                style={{
                  width: '100%',
                  height: '800px',
                  border: 'none',
                  borderRadius: '0.5rem',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                }}
                title={t("tools.aiToolEvaluationPromptGenerator.name")}
              />
            )}
          </div>

          {/* Additional Information */}
          <div className="mt-12 space-y-6">
            <div className="bg-muted/30 rounded-lg p-6 backdrop-blur-sm animate-fade-in" style={{ animationDelay: "300ms" }}>
              <h3 className="text-lg font-semibold mb-3">About This Tool</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {t("tools.aiToolEvaluationPromptGenerator.content.overview")}
              </p>
            </div>

            <div className="bg-primary/5 rounded-lg p-6 border border-primary/20 animate-fade-in" style={{ animationDelay: "400ms" }}>
              <h3 className="text-lg font-semibold mb-3">How to Use</h3>
              <ol className="text-sm text-muted-foreground space-y-2 list-decimal list-inside">
                <li>Select your startup phase - Each phase has different evaluation priorities</li>
                <li>Choose your business model - Optional but provides additional context</li>
                <li>Describe your specific need - Be specific about what problem you're trying to solve</li>
                <li>Generate and copy the prompt - Use it with any LLM like ChatGPT, Claude, or Gemini</li>
              </ol>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AIToolEvaluationPromptGenerator;
